import { PlainNotificationEndpointModel } from '@logz-pkg/models';
import { agentsApiService } from '../agents/api.service';
import { plainNotificationEndpointApiService } from '../notification-endpoint/api.service';

export interface IRcaHydrationInput {
  id: string | number | undefined;
  rcaNotificationEmails: readonly string[] | undefined;
  rcaNotificationEndpointIds: readonly (string | number)[] | undefined;
  useAlertNotificationEndpointsForRca: boolean;
  mainEmails: readonly string[] | undefined;
}

export interface IRcaHydrationResult {
  emails: string[];
  useAlertNotificationEndpointsForRca: boolean;
}

interface IResolveRcaEndpointIdsInput {
  rcaNotificationEndpoints?: ReadonlyArray<PlainNotificationEndpointModel> | null;
  rcaNotificationEndpointIds?: ReadonlyArray<number | string> | null;
}

export const resolveRcaEndpointIds = ({
  rcaNotificationEndpoints,
  rcaNotificationEndpointIds,
}: IResolveRcaEndpointIdsInput): number[] => {
  const fromObjects = (rcaNotificationEndpoints ?? []).map(endpoint => Number(endpoint.id));
  const fromIds = (rcaNotificationEndpointIds ?? []).map(Number);

  return fromObjects.length > 0 ? fromObjects : fromIds;
};

export const fetchNotificationEndpointsByIds = async (
  ids: readonly (string | number)[] | null | undefined,
): Promise<PlainNotificationEndpointModel[]> => {
  if (!ids?.length) return [];

  const { results } = await plainNotificationEndpointApiService.searchByEndpointIds(ids.map(Number));

  const endpointsById = new Map(results.map(endpoint => [endpoint.id, endpoint]));

  return ids.flatMap(id => {
    const endpoint = endpointsById.get(Number(id));

    return endpoint ? [endpoint] : [];
  });
};

const sameEmails = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;

  const as = [...a].sort();
  const bs = [...b].sort();

  return as.every((value, i) => value === bs[i]);
};

export const hydrateRcaRecipients = async (input: IRcaHydrationInput): Promise<IRcaHydrationResult> => {
  const fromBackend = input.rcaNotificationEmails ?? [];
  const savedFlag = input.useAlertNotificationEndpointsForRca;

  if (fromBackend.length > 0) {
    return { emails: [...fromBackend], useAlertNotificationEndpointsForRca: savedFlag };
  }

  if (input.id === undefined || input.id === null || input.id === '') {
    return { emails: [], useAlertNotificationEndpointsForRca: savedFlag };
  }

  const agent = await agentsApiService.getAgentByAlertId(String(input.id)).catch(() => null);
  const agentEmails: string[] = Array.isArray(agent?.emails) ? agent!.emails! : [];

  if (agentEmails.length === 0) {
    return { emails: [], useAlertNotificationEndpointsForRca: savedFlag };
  }

  if ((input.rcaNotificationEndpointIds?.length ?? 0) > 0) {
    return { emails: [...agentEmails], useAlertNotificationEndpointsForRca: false };
  }

  const mainEmails = input.mainEmails ?? [];

  if (savedFlag === true && sameEmails(agentEmails, mainEmails)) {
    return { emails: [], useAlertNotificationEndpointsForRca: true };
  }

  return { emails: [...agentEmails], useAlertNotificationEndpointsForRca: false };
};
